import i18n from 'i18next';
import { createSelector } from 'reselect';
import _ from 'lodash';
import { SOURCE } from '@meiqia/constants';
import { isChinaProvince } from '@meiqia/utils/province';

const { allMap } = SOURCE;

const genTreeNode = ({ value, title, ...extra }) => ({
  title,
  label: title,
  value,
  key: value,
  ...extra
});

const deviceMap = {
  other: i18n.t('其他'),
  mobile: i18n.t('手机'),
  pc: i18n.t('桌面')
};
const getLabel = (map, key) => (map && map[key]) || key;

const transTree = ({ list, map, count }) => {
  const result = [];
  _.forIn(list, (value, key) => {
    const valueList = value.filter(item => {
      return !!item;
    });
    const label = getLabel(map, key);
    if (valueList.length > 0) {
      result.push({
        ...genTreeNode({ value: key, title: label, label: label, count: count[key] || 0 }),
        children: valueList.map(val =>
          genTreeNode({
            value: val,
            title: getLabel(map, val),
            label: getLabel(map, val),
            count: count[val] || 0
          })
        )
      });
    } else {
      result.push({
        ...genTreeNode({ value: key, title: label, label: label, count: count[key] || 0 })
      });
    }
  });
  return result;
};

const OVERSEA = i18n.t('国外');
const MOBILE_PLATFORM = ['mobile', 'tablet'];
const isMobile = platform => MOBILE_PLATFORM.includes(platform);
const PC_PLATFORM = ['pc', 'bot', 'other'];
const isPc = platform => PC_PLATFORM.includes(platform);

const getFilterCondition = createSelector([data => data], visitors => {
  const localsHash = {};
  const osFamilyHash = {};
  const sourceHash = {};
  const localsCount = {};
  const osFamilyCount = {};
  const sourceCount = {};
  const addCount = (key, count) => {
    if (count[key]) {
      count[key] += 1;
    } else {
      count[key] = 1;
    }
  };

  visitors.forEach(val => {
    const { province, platform, os_family, country, sourceInfo } = val;

    // 区域
    if (isChinaProvince(province) || country === i18n.t('中国')) {
      const { city } = val;

      if (localsHash[province]) {
        if (!localsHash[province].includes(city)) {
          localsHash[province].push(city);
        }
      } else {
        localsHash[province] = [];
        localsHash[province].push(city);
      }

      if (province !== city) {
        addCount(province, localsCount);
        addCount(city, localsCount);
      } else {
        addCount(province, localsCount);
      }
    } else {
      const { country } = val;
      if (localsHash[OVERSEA]) {
        if (!localsHash[OVERSEA].includes(country)) {
          localsHash[OVERSEA].push(country);
        }
      } else {
        localsHash[OVERSEA] = [];
        localsHash[OVERSEA].push(country);
      }

      addCount(OVERSEA, localsCount);
      addCount(country, localsCount);
    }

    // 设备
    if (isMobile(platform)) {
      if (osFamilyHash['mobile']) {
        if (!osFamilyHash['mobile'].includes(os_family)) {
          osFamilyHash['mobile'].push(os_family);
        }
      } else {
        osFamilyHash['mobile'] = [];
        osFamilyHash['mobile'].push(os_family);
      }

      addCount('mobile', osFamilyCount);
      addCount(os_family, osFamilyCount);
    } else if (isPc(platform)) {
      if (osFamilyHash['pc']) {
        if (!osFamilyHash['pc'].includes(os_family)) {
          osFamilyHash['pc'].push(os_family);
        }
      } else {
        osFamilyHash['pc'] = [];
        osFamilyHash['pc'].push(os_family);
      }
      addCount('pc', osFamilyCount);
      addCount(os_family, osFamilyCount);
    }

    // 来源
    if (sourceInfo.source) {
      const { source, subSource } = sourceInfo;
      if (sourceHash[source]) {
        if (!sourceHash[source].includes(subSource)) {
          sourceHash[source].push(subSource);
        }
      } else {
        sourceHash[source] = [];
        sourceHash[source].push(subSource);
      }
      if (source) {
        addCount(source, sourceCount);
      }
      if (subSource) {
        addCount(subSource, sourceCount);
      }
    }
  });

  return {
    localsHash,
    osFamilyHash,
    sourceHash,
    localsCount,
    osFamilyCount,
    sourceCount
  };
});

const getFilterParam = createSelector([data => getFilterCondition(data), data => data], (condition, data) => {
  const { localsHash, osFamilyHash, sourceHash, localsCount, osFamilyCount, sourceCount } = condition;
  const region = transTree({ list: localsHash, count: localsCount });
  const device = transTree({ list: osFamilyHash, map: deviceMap, count: osFamilyCount });
  const source = transTree({ list: sourceHash, map: allMap, count: sourceCount });
  region.unshift({
    label: i18n.t('全部'),
    title: i18n.t('全部'),
    count: data.length || 0,
    value: '',
    key: ''
  });
  return {
    region,
    device,
    source
  };
});

const parseVisitor = createSelector([data => data], data => {
  return _.reverse(_.sortBy(data, 'update_on'));
});

const inCondition = (visitor, filters) => {
  let matching = true;
  // 根据当前条件过滤出来访客是否应该加入列表
  for (let key in filters) {
    let value = filters[key];
    // 为空的时候不做任何匹配
    if (!_.isEmpty(value)) {
      switch (key) {
        case 'region':
          if (value?.province) {
            if (visitor.province !== value.province) matching = false;
            break;
          }
          if (value?.city) {
            if (visitor.city !== value.city) matching = false;
            break;
          }
          if (value?.country) {
            if (value?.country !== '国外') {
              if (visitor.country !== value.country) matching = false;
              break;
            } else if (visitor?.country === '中国') {
              matching = false;
              break;
            }
          }

          break;

        case 'device':
          if (value.filter(v => visitor.os_family.indexOf(v) > -1).length === 0) matching = false;
          break;

        case 'engine':
          // 这个是前端 initVisitor 后才添加上的 key
          const equal =
            value.filter(val => visitor.sourceInfo?.source === val || visitor.sourceInfo?.subSource === val).length !==
            0;
          if (!equal) {
            matching = false;
          }
          break;
        default:
          matching = true;
      }
    }
  }
  return matching;
};

const filterVisitor = createSelector([data => data, (data, condition) => condition], (data, condition) => {
  return data.filter(val => inCondition(val, condition));
});

export default {
  getFilterParam,
  parseVisitor,
  filterVisitor,
  getFilterCondition
};
